import { useEffect, useRef } from 'react'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import TextHeadline from '../ui/texts/TextHeadline'
import LoadingBar from '../ui/LoadingBar'
import Button from '../ui/buttons/Button'
import { CARD_LANDING_CRAWL_DURATION_MS, useLoadingProgressController } from '@/src/hooks/useLoadingProgressController'

export type CardLandingLoadingScreenProps = {
    assetProgress: number
    loadingReady: boolean
    isLoadingError: boolean
    onRetryLoading: () => void
    onBarComplete: () => void
    isParentRecipient: boolean
    className?: string
}

const cardVariants = {
    hidden: { scale: 0.85, opacity: 0, y: 20 },
    visible: {
        scale: 1,
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring' as const,
            stiffness: 380,
            damping: 22,
            mass: 0.8,
        },
    },
    exit: {
        scale: 0.9,
        opacity: 0,
        transition: { duration: 0.2 },
    },
}

export default function CardLandingLoadingScreen({
    assetProgress,
    loadingReady,
    isLoadingError,
    onRetryLoading,
    onBarComplete,
    className,
    isParentRecipient,
}: CardLandingLoadingScreenProps) {
    const completedRef = useRef(false)
    const isStele = useSteleBreakpoint()
    const { displayProgress, durationMS, isComplete } = useLoadingProgressController(
        assetProgress,
        loadingReady,
        CARD_LANDING_CRAWL_DURATION_MS,
    )

    useEffect(() => {
        if (!isComplete || completedRef.current) {
            return
        }
        completedRef.current = true
        onBarComplete()
    }, [isComplete, onBarComplete])

    return (
        <motion.div
            className={clsx(
                'pointer-events-auto absolute inset-0 z-[90] overflow-hidden bg-white',
                className,
            )}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
        >
            <img
                src='/images/parentsLanding/background.png'
                alt=''
                className='absolute inset-0 h-full w-full object-cover'
            />
            <div
                className='pointer-events-none absolute inset-0'
                style={{
                    backgroundImage:
                        'linear-gradient(180deg, rgba(255, 255, 255, 0.65) 7%, rgba(255, 255, 255, 0) 30%), linear-gradient(180deg, rgba(252, 147, 104, 0) 46%, rgba(126, 74, 52, 0.45) 63%, rgba(0, 0, 0, 0.65) 100%)',
                }}
            />

            <div
                className={clsx(
                    'relative flex h-full flex-col items-center justify-between',
                    'px-4 pb-6 pt-8',
                    'stele:px-16 stele:pb-24 stele:pt-32',
                )}
            >
                <div className='flex flex-col items-center text-center'>
                    <TextHeadline
                        colorPreset='red'
                        fontSize={isStele ? 96 : 24}
                        leading={0.95}
                    >
                        {isParentRecipient ? 'Dein Kind ist durch\'s ganze Leben gelaufen!' : ''}
                    </TextHeadline>
                </div>

                <AnimatePresence mode='wait'>
                    <motion.div
                        key='card-loading-screen'
                        className={clsx(
                            'relative flex shrink-0 items-center justify-center',
                            'w-[min(92vw,22.5rem)] aspect-[375/500]',
                            'stele:w-[min(80vw,56rem)]',
                        )}
                        variants={cardVariants}
                        initial='hidden'
                        animate='visible'
                        exit='exit'
                    >
                        <div className='absolute inset-x-[8%] inset-y-[6.67%] overflow-hidden'>
                            <img
                                src='/images/parentsLanding/card-background.png'
                                alt=''
                                className='h-full w-full object-cover'
                            />
                        </div>
                        <img
                            src='/images/parentsLanding/card-frame.png'
                            alt=''
                            className='pointer-events-none absolute inset-0 h-full w-full object-contain'
                        />
                        <img
                            src='/images/LogoStacked.png'
                            alt='Life Rush'
                            className={clsx(
                                'pointer-events-none absolute object-contain',
                                'w-[75%]',
                                'stele:w-[38%]',
                            )}
                        />
                    </motion.div>
                </AnimatePresence>

                <div
                    className={clsx(
                        'flex w-full flex-col items-center',
                        'max-w-[20.5rem] gap-2',
                        'stele:max-w-412 stele:gap-8',
                    )}
                >
                    {isLoadingError && (
                        <Button onClick={onRetryLoading}>
                            Erneut versuchen
                        </Button>
                    )}
                    <LoadingBar progress={displayProgress} duration={durationMS} className='mb-4' />
                </div>
            </div>
        </motion.div>
    )
}


